import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rvFields", "cabinFields", "primitiveFields", "uploadProgress", "progressBar"]
  static values = {
    successUrl: { type: String, default: "/admin/rentals" }
  }

  connect() {
    this.updateDynamicFields()
    this.updateRequiredFields()
    this.updateStatusIndicator()
    console.log("Rental form controller connected")
  }

  toggleFields(event) {
    this.updateDynamicFields()
    this.updateRequiredFields()
  }

  statusChanged(event) {
    this.updateStatusIndicator()
  }

  updateDynamicFields() {
    const siteTypeSelect = this.element.querySelector('select[name*="campsite_type"]')
    if (!siteTypeSelect) return

    const selectedType = siteTypeSelect.value

    // Hide all dynamic fields first
    this.hideAllDynamicFields()

    // Show the relevant fields based on the selected type
    switch(selectedType) {
      case 'primitive_camping':
        this.primitiveFieldsTarget.classList.remove('hidden')
        break
      case 'rv_camping':
        this.rvFieldsTarget.classList.remove('hidden')
        break
      case 'cabin':
        this.cabinFieldsTarget.classList.remove('hidden')
        break
    }

    // Trigger a resize event for any nested forms
    window.dispatchEvent(new Event('resize'))
  }

  updateRequiredFields() {
    const siteTypeSelect = this.element.querySelector('select[name*="campsite_type"]')
    if (!siteTypeSelect) return

    const selectedType = siteTypeSelect.value

    // Get all form fields
    const primitiveFields = this.primitiveFieldsTarget.querySelectorAll('select, input:not([type="hidden"])')
    const rvFields = this.rvFieldsTarget.querySelectorAll('select, input:not([type="hidden"])')
    const cabinFields = this.cabinFieldsTarget.querySelectorAll('select, input:not([type="hidden"]):not([name*="rental_beds"])')

    // Reset all fields to not required
    primitiveFields.forEach(field => field.required = false)
    rvFields.forEach(field => field.required = false)
    cabinFields.forEach(field => field.required = false)

    // Set required fields based on selected type
    switch(selectedType) {
      case 'primitive_camping':
        primitiveFields.forEach(field => field.required = true)
        break
      case 'rv_camping':
        rvFields.forEach(field => field.required = true)
        break
      case 'cabin':
        cabinFields.forEach(field => field.required = true)
        break
    }
  }

  hideAllDynamicFields() {
    this.rvFieldsTarget.classList.add('hidden')
    this.cabinFieldsTarget.classList.add('hidden')
    this.primitiveFieldsTarget.classList.add('hidden')
  }

  siteTypeChanged(event) {
    this.toggleFields(event)
  }

  async removeImage(event) {
    event.preventDefault()
    const button = event.currentTarget
    const imageId = button.dataset.imageId
    const rentalId = this.element.querySelector('input[name="rental[id]"]')?.value

    if (!imageId || !rentalId) return

    try {
      const response = await fetch(`/admin/rentals/${rentalId}/remove_image`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({ image_id: imageId })
      })

      if (response.ok) {
        button.closest('.aspect-square').remove()
      } else {
        console.error('Failed to remove image')
      }
    } catch (error) {
      console.error('Error removing image:', error)
    }
  }

  handleSubmit(event) {
    // Handle form submission
    console.log("Form submitted", event.detail)
    
    if (event.detail && event.detail.success) {
      if (this.successUrlValue) {
        window.location.href = this.successUrlValue
      }
    }
  }

  updateStatusIndicator() {
    const statusSelect = this.element.querySelector('select[name*="status"]')
    if (!statusSelect) return

    const statusIndicator = this.element.querySelector('.status-indicator')
    if (!statusIndicator) return

    const status = statusSelect.value
    const icon = statusIndicator.querySelector('svg')
    const mainText = statusIndicator.querySelector('.status-text')
    const subText = statusIndicator.querySelector('.status-subtext')

    // Update icon and text based on status
    switch(status) {
      case 'active':
        icon.innerHTML = '<path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clip-rule="evenodd" />'
        icon.classList.remove('text-red-500', 'text-yellow-500')
        icon.classList.add('text-green-500')
        mainText.textContent = 'This rental is currently active and available for booking'
        subText.textContent = 'Guests can view and book this rental'
        break
      case 'maintenance':
        icon.innerHTML = '<path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clip-rule="evenodd" />'
        icon.classList.remove('text-green-500', 'text-red-500')
        icon.classList.add('text-yellow-500')
        mainText.textContent = 'This rental is currently under maintenance and cannot be booked'
        subText.textContent = 'The rental will be hidden until maintenance is complete'
        break
      case 'inactive':
        icon.innerHTML = '<path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z" clip-rule="evenodd" />'
        icon.classList.remove('text-green-500', 'text-yellow-500')
        icon.classList.add('text-red-500')
        mainText.textContent = 'This rental is currently inactive and hidden from the booking system'
        subText.textContent = 'The rental will be completely hidden from the site'
        break
    }
  }
}
